import axios from 'axios';
import { ENDPOINTS, EVENTS } from './constants';

/* =======================
   IDLE HELPER
======================= */
function idle() {
  if ('requestIdleCallback' in window) {
    return new Promise((r) => requestIdleCallback(r, { timeout: 50 }));
  } else {
    return new Promise((r) => setTimeout(r, 0));
  }
}

/* =======================
   NETWORK MANAGER
======================= */

class NetworkManager {
  constructor() {
    this.api = axios.create({
      baseURL: ENDPOINTS.CORE?.BASE || '',
      timeout: 30_000,
      headers: { 'Content-Type': 'application/json' }
    });

    this.abortControllers = new Map();
  }

  setup(bus) {
    bus.on(EVENTS.NETWORK.REQUEST, (payload) =>
      this.handleNetworkRequest(bus, payload)
    );

    return { cancelRequest: this.cancelRequest.bind(this) };
  }

  async handleNetworkRequest(bus, payload) {
    const { id } = payload;

    this.emitRequestStart(bus, payload);

    const controller = new AbortController();
    this.abortControllers.set(id, controller);

    const result = await this.sendRequest({
      payload,
      signal: controller.signal
    });

    this.abortControllers.delete(id);

    bus.emit(EVENTS.NETWORK.RESPONSE, result);
  }

  async sendRequest({ payload, signal }) {
    let { id, url, method = 'GET', data } = payload;


    const isFullUrl = /^https?:\/\//i.test(url);
    const requestConfig = { url, method, data, signal };
    if (isFullUrl) requestConfig.baseURL = '';

    try {
      const response = await this.api(requestConfig);

      // ── PETITES RÉPONSES → NORMAL
      if (typeof response.data !== 'string' || response.data.length < 300_000) {
        return { id, data: response.data };
      }

      // ── GROS JSON → PARSE IDLE PAR TRANCHES
      const text = response.data;
      const items = [];

      let depth = 0;
      let start = 0;

      for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (c === '{') depth++;
        if (c === '}') depth--;

        if (depth === 0 && c === '}') {
          const slice = text.slice(start, i + 1);
          start = i + 1;

          items.push(JSON.parse(slice));

          if (items.length % 50 === 0) {
            await idle();
          }
        }
      }

      return { id, data: items };
    } catch (error) {
      if (axios.isCancel(error)) return { id, canceled: true };
      return { id, error: error.response?.data ?? error.message };
    }
  }

  cancelRequest(id) {
    const controller = this.abortControllers.get(id);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(id);
    }
  }

  emitRequestStart(bus, payload) {
    const { id, url, method = 'GET', data, poll } = payload;
    bus.emit(EVENTS.NETWORK.REQUEST_START, {
      id,
      url,
      method,
      data,
      poll,
      timestamp: Date.now()
    });
  }
}

const networkManager = new NetworkManager();
export const setupNetwork = networkManager.setup.bind(networkManager);
